package horcrux.frontend.common.views.chat

import horcrux.common.api.dtos.types.CollectionName
import horcrux.common.api.dtos.types.ContextDocumentChunkAndMetadata
import horcrux.common.utils.eitherWay
import horcrux.frontend.common.BooleanState
import horcrux.frontend.common.GlobalAppState
import horcrux.frontend.common.MaybeState
import horcrux.frontend.common.configs.StaticContentConfigs
import horcrux.frontend.common.views.chat.context.ContextDocumentChunks
import horcrux.frontend.common.views.collections.editcollectionmenu.EditCollectionName
import horcrux.frontend.common.views.components.containers.DialogAction
import horcrux.frontend.common.views.components.containers.DialogView
import horcrux.frontend.common.views.components.containers.HStack
import mui.material.AlertColor
import mui.material.Button
import mui.material.ButtonVariant
import mui.material.DialogContentText
import mui.material.DialogTitle
import mui.material.Typography
import mui.material.styles.TypographyVariant
import mui.system.sx
import react.ChildrenBuilder
import react.FC
import react.PropsWithChildren
import react.ReactNode
import react.create
import web.cssom.AlignItems
import web.cssom.Display
import web.cssom.FlexDirection
import web.cssom.FontFamily
import web.cssom.pt

sealed interface MessageSenderData {
    val avatarLabel: String
    val contextDocumentChunkAndMetadataList: List<ContextDocumentChunkAndMetadata>
    fun ChildrenBuilder.messageSender(
        globalAppState: GlobalAppState, contextDocumentChunkAndMetadataList: List<ContextDocumentChunkAndMetadata>
    )

    fun messageSender(childrenBuilder: ChildrenBuilder, globalAppState: GlobalAppState) =
        childrenBuilder.messageSender(globalAppState, contextDocumentChunkAndMetadataList)
}

data class TheGrokApp(
    override val contextDocumentChunkAndMetadataList: List<ContextDocumentChunkAndMetadata>
) : MessageSenderData {
    override val avatarLabel: String = "The Grok App"

    override fun ChildrenBuilder.messageSender(
        globalAppState: GlobalAppState,
        contextDocumentChunkAndMetadataList: List<ContextDocumentChunkAndMetadata>,
    ) {
        val showSourcesDialogState = BooleanState()
        val showPublishDialogState = BooleanState()
        ContextDocumentChunks {
            this.contextDocumentChunkAndMetadataList = contextDocumentChunkAndMetadataList
            this.showSourcesDialogState = showSourcesDialogState
            this.globalAppState = globalAppState
        }
        DialogView {
            val collectionNameMaybeState = MaybeState<CollectionName>()
            this.showDialogState = showPublishDialogState
            this.title = "Publish Your Chat"
            this.dialogActionsMaybe = listOf(
                DialogAction("Cancel") { _, _ -> showPublishDialogState.setFalse() },
                DialogAction("Publish") { showDialogState, isLoadingState ->
                    globalAppState.submitBackendAPIRequestAsync { backendClient ->
                        isLoadingState.setTrue()
                        globalAppState.currentCollectionMetadataMaybeState.get()?.let { collectionMetadata ->
                            globalAppState.updateBanner(AlertColor.info, "Publishing your chat to the public internet")
                            backendClient.publishCollection(
                                collectionID = collectionMetadata.id,
                                chatMessageDataList = globalAppState.currentChatMessageDataList,
                                collectionNameMaybe = collectionNameMaybeState.get(),
                            )
                                .onRight { response ->
                                    collectionNameMaybeState.reset()
                                    globalAppState.chatPageMetadataByIDState.update { it.add(response.chatPageMetadata) }
                                    globalAppState.currentChatPageMetadataMaybeState.set(response.chatPageMetadata)
                                    globalAppState.updateBanner(AlertColor.success, "Successfully published your chat!")
                                    globalAppState.collectionNavigatorState.showManagePublicCollectionsState.setTrue()
                                }
                                .eitherWay {
                                    isLoadingState.setFalse()
                                    showDialogState.setFalse()
                                }
                                .bind()
                        } ?: run {
                            globalAppState.updateBanner(
                                AlertColor.error, "There was an unexpected error. CollectionID is not defined."
                            )
                        }
                    }
                }
            )
            DialogTitle { +"Publish Your Chat" }
            DialogContentText { +"Click the below button to publish your collection and any chat history above and including the message on which you clicked publish. You will then receive a link, which you can use to share the created chat page with others, and they can pick up where you left off in your conversation. If you change your mind you can delete it later. However your documents will no longer be private. People can download and import them, which you cannot undo. You can also give your public chat page collection a new name, or we can re-use the one from your private collection. Note we will not publish any messages in this chat that were answered with documents you later deleted." }
            EditCollectionName { this.textFieldValueMaybeState = collectionNameMaybeState }
        }
        MessageSender {
            this.messageSenderData = this@TheGrokApp
            this.avatar = GrokAppAvatar.create {
                this.messageSenderData = this@TheGrokApp
            }
            listOf(
                Pair("View Sources") { showSourcesDialogState.setTrue() },
                Pair("Publish") { showPublishDialogState.setTrue() },
            ).forEach { p ->
                +Button.create {
                    +p.first
                    this.variant = ButtonVariant.text
                    this.sx {
                        this.fontFamily = FontFamily.sansSerif
                        this.marginLeft = 5.pt
                        this.color = StaticContentConfigs.BLUE
                    }
                    this.onClick = {
                        it.preventDefault()
                        p.second()
                    }
                }
            }
        }
    }
}

class User(emailMaybe: String?) : MessageSenderData {
    constructor() : this(null)

    override val avatarLabel: String = "You"
    override val contextDocumentChunkAndMetadataList: List<ContextDocumentChunkAndMetadata> = listOf()
    val email: String = emailMaybe ?: avatarLabel

    override fun ChildrenBuilder.messageSender(
        globalAppState: GlobalAppState,
        contextDocumentChunkAndMetadataList: List<ContextDocumentChunkAndMetadata>,
    ) = MessageSender {
        this.messageSenderData = this@User
        this.avatar = UserAvatar.create {
            this.messageSenderData = this@User
        }
    }
}

external interface MessageSenderProps : PropsWithChildren {
    var messageSenderData: MessageSenderData
    var avatar: ReactNode
}

val MessageSender = FC<MessageSenderProps> { props ->
    HStack {
        this.sx {
            this.display = Display.flex
            this.flexDirection = FlexDirection.row
            this.alignItems = AlignItems.center
        }
        +props.avatar
        Typography {
            +props.messageSenderData.avatarLabel
            this.variant = TypographyVariant.h6
            this.sx {
                this.fontFamily = FontFamily.sansSerif
                this.marginLeft = 3.pt
            }
        }
        +props.children
    }
}
